import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader, LoadingSpinner } from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';
import { dealerCreditApi, dealerCommissionSettingsApi } from '@/services/api';
import type { Order, OrderStatus } from '@/types';
import clsx from 'clsx';

const statusColors: Record<OrderStatus, string> = {
  draft: 'bg-gray-600',
  pending_payment: 'bg-yellow-600',
  submitted: 'bg-blue-600',
  pending_release: 'bg-orange-600',
  approved: 'bg-blue-500',
  in_production: 'bg-orange-500',
  ready_to_ship: 'bg-purple-600',
  shipped: 'bg-teal-600',
  delivered: 'bg-green-600',
  install_scheduled: 'bg-blue-500',
  installed: 'bg-green-600',
  warranty_registered: 'bg-green-600',
  completed: 'bg-green-800',
  closed: 'bg-gray-800',
  cancelled: 'bg-red-600',
  on_hold: 'bg-orange-600',
};

const statusLabels: Record<OrderStatus, string> = {
  draft: 'Draft',
  pending_payment: 'Pending Payment',
  submitted: 'Submitted',
  pending_release: 'Pending Release',
  approved: 'Approved',
  in_production: 'In Production',
  ready_to_ship: 'Ready to Ship',
  shipped: 'Shipped',
  delivered: 'Delivered',
  install_scheduled: 'Install Scheduled',
  installed: 'Installed',
  warranty_registered: 'Warranty Registered',
  completed: 'Completed',
  closed: 'Closed',
  cancelled: 'Cancelled',
  on_hold: 'On Hold',
};

function StatusBadge({ status }: { status: OrderStatus }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white',
        statusColors[status]
      )}
    >
      {statusLabels[status]}
    </span>
  );
}

function StatCard({
  title,
  value,
  subtitle,
  color = 'yellow',
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  color?: 'yellow' | 'blue' | 'green' | 'purple';
}) {
  const colorClasses = {
    yellow: 'border-keder-yellow text-keder-yellow',
    blue: 'border-blue-500 text-blue-500',
    green: 'border-green-500 text-green-500',
    purple: 'border-purple-500 text-purple-500',
  };

  return (
    <div className={clsx('card border-l-4', colorClasses[color])}>
      <p className="text-gray-400 text-sm">{title}</p>
      <p className="text-3xl font-bold text-white mt-1">{value}</p>
      {subtitle && <p className="text-gray-500 text-sm mt-1">{subtitle}</p>}
    </div>
  );
}

function RecentOrdersTable({ orders }: { orders: Order[] }) {
  if (orders.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <p>No recent orders</p>
        <Link to="/orders/new" className="text-keder-yellow hover:underline mt-2 inline-block">
          Create your first order
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full table-fixed">
          <colgroup>
            <col style={{ width: '15%' }} />
            <col style={{ width: '30%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '20%' }} />
          </colgroup>
          <thead>
            <tr className="text-left text-gray-400 text-sm border-b border-keder-gray">
              <th className="pb-3 font-medium">Order #</th>
              <th className="pb-3 font-medium">Sidemark</th>
              <th className="pb-3 font-medium">Status</th>
              <th className="pb-3 font-medium text-right">Total</th>
              <th className="pb-3 font-medium text-right">Date</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.id} className="border-b border-keder-gray/50 hover:bg-keder-gray/20">
                <td className="py-3">
                  <Link to={`/orders/${order.id}`} className="text-keder-yellow hover:underline">
                    {order.orderNumber}
                  </Link>
                </td>
                <td className="py-3 text-white truncate">{order.sidemark || '-'}</td>
                <td className="py-3">
                  <StatusBadge status={order.status} />
                </td>
                <td className="py-3 text-white text-right">${order.totalAmount.toLocaleString()}</td>
                <td className="py-3 text-gray-400 text-right">
                  {new Date(order.createdAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {orders.map((order) => (
          <Link
            key={order.id}
            to={`/orders/${order.id}`}
            className="block bg-keder-gray/30 rounded-lg p-4 hover:bg-keder-gray/50 transition-colors"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-keder-yellow font-medium">{order.orderNumber}</span>
              <StatusBadge status={order.status} />
            </div>
            <div className="text-white text-sm mb-1 truncate">
              {order.sidemark || 'No sidemark'}
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">
                {new Date(order.createdAt).toLocaleDateString()}
              </span>
              <span className="text-white font-medium">
                ${order.totalAmount.toLocaleString()}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </>
  );
}

export function DashboardPage() {
  const { user } = useAuth();

  // Fetch dashboard stats (accurate counts from all orders)
  const { data: stats } = useQuery({
    queryKey: ['orders', 'dashboard-stats'],
    queryFn: () => api.getDashboardStats(),
  });

  // Fetch recent orders for the table
  const { data: ordersData, isLoading } = useQuery({
    queryKey: ['orders', 'recent'],
    queryFn: () => api.getOrders({ limit: 5 }),
  });

  const orders = ordersData?.items || [];

  // Dealer commission settings — drives whether to show the "Record Your Sales"
  // prompt at all. Dealers who don't run an internal commission program
  // (commission_tracking_enabled = false) shouldn't see this nag. Failsafe to
  // ON if the request fails so it doesn't silently drop on a transient error.
  const { data: commissionSettings } = useQuery({
    queryKey: ['dealer', 'commission-settings'],
    queryFn: () => dealerCommissionSettingsApi.get().catch(() => null),
    staleTime: 5 * 60 * 1000,
  });
  const trackingEnabled = commissionSettings?.commission_tracking_enabled ?? true;

  // Count orders that need sell prices recorded (shipped/delivered without customerSellPrice)
  const { data: allOrdersData } = useQuery({
    queryKey: ['orders', 'all-for-sell-price'],
    queryFn: () => api.getOrders({ limit: 100 }),
    enabled: trackingEnabled,
  });

  const ordersNeedingSellPrice = trackingEnabled ? (allOrdersData?.items || []).filter((order: Order) => {
    const o = order as any;
    const eligibleStatuses = ['shipped', 'delivered', 'install_scheduled', 'installed', 'warranty_registered', 'completed', 'closed'];
    const sellPrice = o.customerSellPrice || o.customer_sell_price;
    return eligibleStatuses.includes(order.status) && (!sellPrice || sellPrice <= 0);
  }) : [];

  // Fetch credit account info (only if dealer has a net account)
  const { data: creditAccount } = useQuery({
    queryKey: ['dealer-credit', 'my-account'],
    queryFn: () => dealerCreditApi.getMyAccount(),
    retry: false,
  });

  // Use stats from dedicated endpoint for accurate calculations
  const activeOrders = stats?.activeOrders ?? 0;
  const pendingOrders = stats?.pendingOrders ?? 0;
  const inProductionOrders = stats?.inProductionOrders ?? 0;
  const thisMonthTotal = stats?.thisMonthTotal ?? 0;

  return (
    <>
      <PageHeader
        title={`Welcome back, ${user?.firstName || user?.first_name || user?.name?.split(' ')[0] || 'Dealer'}`}
        subtitle={user?.companyName || user?.dealerName}
        actions={
          <Link to="/orders/new" className="btn-primary">
            + New Order
          </Link>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
        <StatCard title="Active Orders" value={activeOrders} color="yellow" />
        <StatCard title="Pending Review" value={pendingOrders} color="blue" />
        <StatCard title="In Production" value={inProductionOrders} color="purple" />
        <StatCard
          title="This Month"
          value={`$${thisMonthTotal.toLocaleString()}`}
          color="green"
        />
      </div>

      {/* Credit Account Card (only for dealers with net account enabled) */}
      {creditAccount?.netAccountEnabled && (
        <div className="mb-6 md:mb-8">
          <div className={clsx(
            'card border-l-4',
            creditAccount.accountStatus === 'frozen' ? 'border-yellow-500' :
            creditAccount.accountStatus === 'suspended' ? 'border-red-500' :
            'border-green-500'
          )}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                Net Credit Account
              </h3>
              <div className="flex items-center gap-2">
                <span className={clsx(
                  'text-xs font-medium px-2 py-0.5 rounded-full',
                  creditAccount.accountStatus === 'active' ? 'bg-green-500/20 text-green-400' :
                  creditAccount.accountStatus === 'frozen' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                )}>
                  {creditAccount.accountStatus === 'active' ? 'Active' :
                   creditAccount.accountStatus === 'frozen' ? 'Frozen' : 'Suspended'}
                </span>
                <span className="text-gray-400 text-sm">Net {creditAccount.netTermsDays}</span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mb-3">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-400">
                  Used: ${creditAccount.creditBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span className="text-gray-400">
                  Limit: ${creditAccount.creditLimit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2.5">
                <div
                  className={clsx(
                    'h-2.5 rounded-full transition-all',
                    creditAccount.creditLimit > 0 && (creditAccount.creditBalance / creditAccount.creditLimit) > 0.9
                      ? 'bg-red-500'
                      : creditAccount.creditLimit > 0 && (creditAccount.creditBalance / creditAccount.creditLimit) > 0.7
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                  )}
                  style={{
                    width: creditAccount.creditLimit > 0
                      ? `${Math.min((creditAccount.creditBalance / creditAccount.creditLimit) * 100, 100)}%`
                      : '0%'
                  }}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-white text-lg font-bold">
                ${creditAccount.creditAvailable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                <span className="text-gray-400 text-sm font-normal ml-1">available</span>
              </p>
              {creditAccount.hasOverdue && (
                <span className="text-xs text-red-400 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  Overdue balance
                </span>
              )}
            </div>

            {creditAccount.accountStatus === 'frozen' && (
              <p className="text-yellow-400 text-xs mt-2">
                Your credit account is currently frozen. New orders require payment. Contact us for assistance.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Unpaid Invoices Alert */}
      {(stats as any)?.unpaidInvoices > 0 && (
        <div className="mb-6 md:mb-8">
          <div className="card border-l-4 border-amber-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold">
                    {(stats as any).unpaidInvoices} Unpaid Invoice{(stats as any).unpaidInvoices > 1 ? 's' : ''}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Outstanding: ${Number((stats as any).outstandingBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
              <Link to="/invoices" className="text-keder-yellow text-sm hover:underline font-medium">
                View Invoices
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Correction Request Alert */}
      {(stats as any)?.pendingCorrections > 0 && (
        <div className="mb-6 md:mb-8">
          <div className="card border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/20 text-red-400 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold">
                    {(stats as any).pendingCorrections} Invoice Correction{(stats as any).pendingCorrections > 1 ? 's' : ''} Pending
                  </h3>
                  <p className="text-gray-400 text-sm">
                    You have correction requests awaiting factory review
                  </p>
                </div>
              </div>
              <Link to="/orders" className="text-keder-yellow text-sm hover:underline font-medium">
                View Orders
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Shipping Adjustments Alert */}
      {(stats as any)?.pendingShippingAdjustments > 0 && (
        <div className="mb-6 md:mb-8">
          <div className="card border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 text-yellow-400 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold">Shipping Adjustment Pending</h3>
                  <p className="text-gray-400 text-sm">A shipping cost has been updated — please review your invoice</p>
                </div>
              </div>
              <Link to="/invoices" className="text-keder-yellow text-sm hover:underline font-medium">
                View
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
        <Link to="/orders/new" className="card hover:border-keder-yellow transition-colors group">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-keder-yellow/20 text-keder-yellow flex items-center justify-center group-hover:bg-keder-yellow group-hover:text-black transition-colors">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-medium">Create New Order</h3>
              <p className="text-gray-400 text-sm">Start a V-Track screen order</p>
            </div>
          </div>
        </Link>

        <Link to="/quote" className="card hover:border-keder-yellow transition-colors group">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-500/20 text-blue-500 flex items-center justify-center group-hover:bg-blue-500 group-hover:text-white transition-colors">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-medium">Quick Quote</h3>
              <p className="text-gray-400 text-sm">Get instant pricing estimates</p>
            </div>
          </div>
        </Link>

        <Link to="/orders" className="card hover:border-keder-yellow transition-colors group">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-500/20 text-green-500 flex items-center justify-center group-hover:bg-green-500 group-hover:text-white transition-colors">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-medium">View All Orders</h3>
              <p className="text-gray-400 text-sm">Track your order history</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Record Sale Reminder */}
      {ordersNeedingSellPrice.length > 0 && (
        <div className="mb-6 md:mb-8">
          <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-4 flex items-start gap-4">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-yellow-400 font-medium">Record Your Sales</h3>
              <p className="text-gray-300 text-sm mt-1">
                {ordersNeedingSellPrice.length} delivered order{ordersNeedingSellPrice.length !== 1 ? 's' : ''} awaiting sale price for commission tracking.
              </p>
            </div>
            <Link
              to="/orders?status=delivered"
              className="flex-shrink-0 text-sm text-yellow-400 hover:text-yellow-300 font-medium flex items-center gap-1"
            >
              View Orders
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      )}

      {/* Recent Orders */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Recent Orders</h2>
          <Link to="/orders" className="text-keder-yellow hover:underline text-sm">
            View all
          </Link>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : (
          <RecentOrdersTable orders={orders} />
        )}
      </div>
    </>
  );
}

export default DashboardPage;
